import '@/i18n/template/util/date.js'
/**
 * 用于将日期时间字符串转换为通用格式，或将通用格式转换为显示用格式
 *
 * 注意： 变量类型与组件的日期时间参数必须是Date对象或时间戳！！
 *
 * 接口的Response字段反显可使用以下函数将日期时间字符串转换为通用格式
 * @see cbpTimeToDate 将CBP日期字符串转换为通用日期格式
 * @see bizTimeToDate 将Biz日期字符串转换为通用日期格式
 *
 * 接口调用时可使用以下函数将通用格式转换为接口需要的格式
 * @see toBizDate 将通用日期格式转换为Biz日期格式
 * @see toBizDateTime 将通用日期时间格式转换为Biz日期时间格式
 * @see toCbpDate 将通用日期格式转换为CBP日期格式
 * @see toCbpTime 将通用时间格式转换为CBP时间格式
 * @see toCbpMonth 将通用月份格式转换为CBP月份格式
 *
 * ***** 通用格式化函数 *****
 * @see formatYearMonth 格式化标准日期格式或Date对象或时间戳
 * @see formatYearAbbrMonth
 * @see formatYearMonthDay  格式化标准日期格式或Date对象或时间戳
 * @see formatYearMonthDayHMS 格式化标准日期格式或Date对象或时间戳
 * @see formatHMS 格式化标准日期格式或Date对象或时间戳
 * @see formatHM
 *
 * ***** 缅甸时间转本地时间格式化函数 *****
 * @see mmFormatYearMonth 格式化缅甸月份
 * @see mmFormatYearMonthDay  格式化缅甸日期
 * @see mmFormatYearMonthDayHMS 格式化缅甸日期时间格式
 * @see mmFormatHMS 格式化缅甸时间格式
 * */

import dayjs from 'dayjs'
import i18n from '@/i18n'
import {
  NeverExpiredDate,
  NeverExpiredTime,
  NeverExpiredDateObject,
  NeverExpiredTime_DateObjet
} from '@/constant'

/**
 * CBP接口 日期时间格式
 */
const CBPMonthTemplate = 'YYYYMM'
const CBPDateTemplate = 'YYYYMMDD'
const CBPTimeTemplate = 'HHmmss'
const CBPDateTimeTemplate = 'YYYYMMDDHHmmss'

/**
 * Biz日期时间格式
 */
const BizDateTemplate = 'YYYY-MM-DD'
const BizDateTimeTemplate = 'YYYY-MM-DD HH:mm:ss'

/**
 * 日期时间格式模板
 */
export const DayJsTemplate = {
  Year: 'YYYY',
  YearMonth: 'MM/YYYY',
  YearAbbrMonth: 'MMM YYYY',
  YearMonthDay: 'DD/MM/YYYY',
  YearMonthDayH: 'DD/MM/YYYY HH',
  YearMonthDayHM: 'DD/MM/YYYY HH:mm',
  YearMonthDayHMS: 'DD/MM/YYYY HH:mm:ss',
  MonthDay: 'DD/MM',
  MonthDayHM: 'DD/MM HH:mm',
  HMS: 'HH:mm:ss',
  HM: 'HH:mm'
}
/**
 * element-ui 日期时间格式模板
 */
export const elementTemplate = {
  Year: 'yyyy',
  YearMonth: 'MM/yyyy',
  YearMonthDay: 'dd/MM/yyyy',
  YearMonthDayH: 'dd/MM/yyyy HH',
  YearMonthDayH00: 'dd/MM/yyyy HH:00',
  YearMonthDayHM: 'dd/MM/yyyy HH:mm',
  YearMonthDayHMS: 'dd/MM/yyyy HH:mm:ss'
}

/**
 * 将CBP日期字符串转换为通用日期格式
 * @description 调CBP接口时，反格式化
 * @param {string} day 格式必须为'YYYYMMDD' 格式
 * @param {string} hms 格式必须为'HHmmss' 格式
 * @returns { Date | null } Date对象 或 null 表示转换失败
 */
export function cbpTimeToDate(day, hms) {
  const str = day + (hms || '')
  if (!/^\d+$/.test(str)) {
    return null
  }
  const year = Number(str.substring(0, 4))
  if (str.length === 4) {
    return new Date(year, 0, 1)
  }
  const month = Number(str.substring(4, 6))
  if (str.length === 6) {
    return new Date(year, month - 1, 1)
  }
  const date = Number(str.substring(6, 8))
  if (str.length === 8) {
    return new Date(year, month - 1, date)
  }
  const hour = Number(str.substring(8, 10))
  if (str.length === 10) {
    return new Date(year, month - 1, date, hour)
  }
  const minute = Number(str.substring(10, 12))
  if (str.length === 12) {
    return new Date(year, month - 1, date, hour, minute)
  }
  if (str.length === 14) {
    const second = Number(str.substring(12, 14))
    return new Date(year, month - 1, date, hour, minute, second)
  }
  return null
}
/**
 * 将Biz日期字符串转换为通用日期格式
 * @description 调Biz接口时，反格式化
 * @param {string} bizDateTime Biz日期字符串，格式为'YYYY-MM-DD HH:mm:ss' 格式
 * @returns { Date | null } Date对象 或 null 表示转换失败
 */
export function bizTimeToDate(bizDateTime) {
  const d = dayjs(bizDateTime)
  if (d.isValid()) {
    return d.toDate()
  }
  return null
}

/**
 * date 格式化为CBP月份格式
 * @description 调CBP接口时，需要格式化为CBP月份格式
 * @param {Date | number | dayjs} date 日期对象或时间戳字
 * @returns {string} CBP月份字符串 例如 '202312'
 */
export function toCbpMonth(date) {
  if (!date) {
    return ''
  }
  return dayjs(date).format(CBPMonthTemplate)
}
/**
 * date 格式化为CBP日期格式
 * @description 调CBP接口时，需要格式化为CBP日期格式
 * @param {Date | number | dayjs} date 日期对象或时间戳字
 * @returns {string} CBP日期字符串 例如 '20231231'
 */
export function toCbpDate(date) {
  if (!date) {
    return ''
  }
  return dayjs(date).format(CBPDateTemplate)
}
/**
 * date 格式化为CBP时间格式
 * @description 调CBP接口时，需要格式化为CBP时间格式
 * @param {Date | number | dayjs} date 日期对象或时间戳字
 * @returns {string} CBP时间字符串 例如 '120101'
 */
export function toCbpTime(date) {
  if (!date) {
    return ''
  }
  return dayjs(date).format(CBPTimeTemplate)
}

/**
 * date 格式化为CBP日期时间格式
 * @description 调CBP接口时，需要格式化为CBP日期时间格式
 * @param {Date | number | dayjs} date 日期对象或时间戳字
 * @returns {string} CBP日期时间字符串 例如 '20231231120101'
 */
export function toCbpDateTime(date) {
  if (!date) {
    return ''
  }
  return dayjs(date).format(CBPDateTimeTemplate)
}

/**
 * date 格式化为Biz日期格式
 * @description 调Biz接口时，需要格式化为Biz日期格式
 * @param {Date | number | dayjs} date 日期对象或时间戳字
 * @returns {string} Biz日期字符串 例如 '2023-12-31'
 */
export function toBizDate(date) {
  if (!date) {
    return ''
  }
  return dayjs(date).format(BizDateTemplate)
}

/**
 * date 格式化为Biz日期时间格式
 * @description 调Biz接口时，需要格式化为Biz日期时间格式
 * @param {Date | number | dayjs} date 日期对象或时间戳字
 * @returns {string} Biz日期时间字符串 例如 '2023-12-31 12:34:56'
 */
export function toBizDateTime(date) {
  if (!date) {
    return ''
  }
  return dayjs(date).format(BizDateTimeTemplate)
}

function formatFactory(dateStr, template) {
  let datejs
  if (typeof dateStr === 'string' && /^\d{4,14}$/.test(dateStr)) {
    let cbpDate = cbpTimeToDate(dateStr)
    if (!cbpDate) {
      return dateStr
    }
    datejs = dayjs(cbpDate)
  } else {
    datejs = dayjs(dateStr)
  }
  if (datejs.isValid()) {
    return datejs.format(template)
  }
  return dateStr
}

/**
 * 仅显示格式化用
 * @description 通用月份格式化给用户显示
 * @param {string | Date | number | dayjs} month 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用月份字符串 例如 '12/2023'
 */
export function formatYearMonth(month) {
  if (!month) return ''
  return formatFactory(month, DayJsTemplate.YearMonth)
}
/**
 * 仅显示格式化用
 * @description 通用缩写月+年份格式化给用户显示
 * @param {string | Date | number | dayjs} month 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用月份字符串 例如 'Mar 2023'
 */
export function formatYearAbbrMonth(month) {
  if (!month) return ''
  return formatFactory(month, DayJsTemplate.YearAbbrMonth)
}
/**
 * 仅显示格式化用
 * @description 通用月份+日期+分钟格式化给用户显示
 * @param {string | Date | number | dayjs} month 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用月份+日期+分钟格式字符串 例如 '12/31 12:34'
 */
export function formatMonthDayHM(month) {
  if (!month) return ''
  return formatFactory(month, DayJsTemplate.MonthDayHM)
}
/**
 * 仅显示格式化用
 * @description 通用日期格式化给用户显示
 * @param {string | Date | number | dayjs} dateTime 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用日期字符串 例如 '31/12/2023'
 */
export function formatYearMonthDay(dateTime) {
  if (!dateTime) return ''
  const date = formatFactory(dateTime, CBPDateTemplate)
  if (date === NeverExpiredDate) {
    return i18n.t('NeverExpire')
  }
  return formatFactory(dateTime, DayJsTemplate.YearMonthDay)
}

/**
 * 仅显示格式化用
 * @description 通用月份+日期格式化给用户显示
 * @param {string | Date | number | dayjs} date 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用月份+日期字符串 例如 '31/01'
 */
export function formatMonthDay(date) {
  if (!date) return ''
  return formatFactory(date, DayJsTemplate.MonthDay)
}

/**
 * 仅显示格式化用
 * @description 通用日期时间格式化给用户显示
 * @param {string | Date | number | dayjs} dateTime 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用日期时间字符串 例如 '31/12/2023 12:34:56'
 */
export function formatYearMonthDayHMS(dateTime) {
  if (!dateTime) return ''
  const date = formatFactory(dateTime, CBPDateTemplate)
  if (date === NeverExpiredDate) {
    return i18n.t('NeverExpire')
  }
  return formatFactory(dateTime, DayJsTemplate.YearMonthDayHMS)
}
/**
 * 仅显示格式化用
 * @description 通用日期+分钟格式化给用户显示
 * @param {string | Date | number | dayjs} dateTime 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用日期时间字符串 例如 '31/12/2023 12:34'
 */
export function formatYearMonthDayHM(dateTime) {
  if (!dateTime) return ''
  const date = formatFactory(dateTime, CBPDateTemplate)
  if (date === NeverExpiredDate) {
    return i18n.t('NeverExpire')
  }
  return formatFactory(dateTime, DayJsTemplate.YearMonthDayHM)
}
/**
 * 仅显示格式化用
 * @description 通用时间格式化给用户显示
 * @param {string | Date | number | dayjs} time 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用时间字符串 例如 '12:34:56'
 */
export function formatHMS(time) {
  if (!time) return ''
  return formatFactory(time, DayJsTemplate.HMS)
}
/**
 * 仅显示格式化用
 * @description 通用时间格式化给用户显示
 * @param {string | Date | number | dayjs} time 标准日期格式或Date对象或时间戳
 * @returns {string} 显示用时间字符串 例如 '12:34'
 */
export function formatHM(time) {
  if (!time) return ''
  return formatFactory(time, DayJsTemplate.HM)
}

/**
 * 转服务器的缅甸时间为本地时区时间
 * @param {string} date 服务器返回的日期字符串 2023-12-31 12:34:56 或者 CBP格式需要完整的日期+时间格式
 * @returns {number} 本地时区时间戳
 */
function convertMyanmarDateToLocal(date) {
  if (!date) return null
  let dateObj = null
  // 检查是否为CBP格式
  if (/^\d{4,14}$/.test(date)) {
    dateObj = cbpTimeToDate(date)
    // 例如20260101 只有日期 没有时分秒 无法计算时区偏移 直接返回
    if (date.length <= 8) {
      return dateObj
    }
  } else {
    dateObj = bizTimeToDate(date)
  }
  if (!dateObj) return null
  // 本地时区偏移（分钟）中国是-480分钟
  const localOffsetMinutes = -new Date().getTimezoneOffset()
  // 缅甸固定偏移：UTC+ 6:30  390 分钟
  const MYANMAR_OFFSET_MINUTES = 390
  const offsetDiff = (localOffsetMinutes - MYANMAR_OFFSET_MINUTES) * 60 * 1000
  const localTime = dateObj.getTime() + offsetDiff
  return localTime
}

/**
 * 转服务器的缅甸时间为本地时区时间并格式化
 * @param {string} date 服务器返回的日期字符串 2023-12-31 12:34:56 或者 CBP格式需要完整的日期+时间格式
 * @returns {string} 显示用日期字符串 例如 '31/12/2023'
 */
export function mmFormatYearMonth(date) {
  let localTime = convertMyanmarDateToLocal(date)
  if (!localTime) return ''
  return formatYearMonth(localTime)
}
/**
 * 转服务器的缅甸时间为本地时区时间并格式化
 * @param {string} date 服务器返回的日期字符串 2023-12-31 12:34:56 或者 CBP格式需要完整的日期+时间格式
 * @returns {string} 显示用日期字符串 例如 '31/12/2023'
 */
export function mmFormatYearMonthDay(date) {
  let localTime = convertMyanmarDateToLocal(date)
  if (!localTime) return ''
  return formatYearMonthDay(localTime)
}

/**
 * 转服务器的缅甸时间为本地时区时间并格式化
 * @param {string} date 服务器返回的日期字符串 2023-12-31 12:34:56 或者 CBP格式需要完整的日期+时间格式
 * @returns {string} 显示用日期时间字符串 例如 '31/12/2023 12:34:56'
 */
export function mmFormatYearMonthDayHMS(date) {
  let localTime = convertMyanmarDateToLocal(date)
  if (!localTime) return ''
  return formatYearMonthDayHMS(localTime)
}

/**
 * 转服务器的缅甸时间为本地时区时间并格式化
 * @param {string} date 服务器返回的日期字符串 2023-12-31 12:34:56 或者 CBP格式需要完整的日期+时间格式
 * @returns {string} 显示用时间字符串 例如 '12:34:56'
 */
export function mmFormatHMS(date) {
  let localTime = convertMyanmarDateToLocal(date)
  if (!localTime) return ''
  return formatHMS(localTime)
}
